/**
 * Mock data for testing crew member deactivation/deletion flows
 * Matches expected API response structure from POST /guide/crew/:id/check_deactivation
 */

// Scenario 1: No experiences assigned, no bookings (also applicable for non-experience roles)
export const MOCK_NO_EXPERIENCES = {
  success: true,
  can_deactivate: true,
  status: "no_experiences_assigned",
  crew_id: 123,
  crew_name: "Alex White",
  role: "Captain",
  assigned_trips: [],
  active_bookings: [],
  requires_substitution: false
}

// Scenario 2: Assigned to experiences but no active bookings
export const MOCK_ASSIGNED_EXPERIENCES_NO_BOOKINGS = {
  success: true,
  can_deactivate: true,
  status: "no_active_bookings",
  crew_id: 124,
  crew_name: "Alex White",
  role: "Captain",
  assigned_trips: [
    {
      trip_id: 45,
      trip_name: "Meet Sunset at Panama City",
      trip_category: "Sightseeing",
      guide_name: "John Doe",
      min_guests: 1,
      max_guests: 6
    },
    {
      trip_id: 46,
      trip_name: "Shell Island Snorkel Tour",
      trip_category: "Snorkeling",
      guide_name: "John Doe",
      min_guests: 2,
      max_guests: 8
    },
    {
      trip_id: 47,
      trip_name: "Dolphin Watch – Gulf Shores",
      trip_category: "Wildlife",
      guide_name: "John Doe",
      min_guests: 1,
      max_guests: 6
    }
  ],
  active_bookings: [],
  requires_substitution: false,
  exp_message: "By deactivating Alex White he will be removed from all assigned experiences availability. In addition, Alex will lose access to the system until reactivated."
}

// Scenario 3: Fully substitutable - All bookings can be auto-substituted
export const MOCK_FULLY_SUBSTITUTABLE = {
  success: true,
  can_deactivate: true,
  status: "fully_substitutable",
  crew_id: 125,
  crew_name: "Alex White",
  role: "Captain",
  assigned_trips: [
    {
      trip_id: 45,
      trip_name: "Meet Sunset at Panama City",
      trip_category: "Sightseeing",
      guide_name: "John Doe",
      min_guests: 1,
      max_guests: 6
    },
    {
      trip_id: 46,
      trip_name: "Shell Island Snorkel Tour",
      trip_category: "Snorkeling",
      guide_name: "John Doe",
      min_guests: 2,
      max_guests: 8
    },
    {
      trip_id: 47,
      trip_name: "Dolphin Watch – Gulf Shores",
      trip_category: "Wildlife",
      guide_name: "John Doe",
      min_guests: 1,
      max_guests: 6
    }
  ],
  active_bookings: [
    {
      booking_id: "12345678",
      booking_uuid: "abc-123",
      experience_name: "Meet Sunset at Panama City",
      client_name: "Tom Smith",
      booked_date: "09/20/2025",
      start_time: "04:00 PM",
      end_time: "08:00 PM",
      status: "booked",
      guest_count: 4,
      available_substitutes: [
        {
          crew_id: 42,
          crew_name: "Boston Whaler 405 Con...",
          role: "Captain"
        },
        {
          crew_id: 43,
          crew_name: "John Martinez",
          role: "Captain"
        }
      ],
      can_substitute: true
    },
    {
      booking_id: "12345671",
      booking_uuid: "def-456",
      experience_name: "Dolphin Watch – Gulf Shores",
      client_name: "Ava Johnson",
      booked_date: "09/21/2025",
      start_time: "06:00 PM",
      end_time: "06:00 PM",
      status: "booked",
      guest_count: 6,
      available_substitutes: [
        {
          crew_id: 42,
          crew_name: "Boston Whaler 405 Con...",
          role: "Captain"
        },
        {
          crew_id: 43,
          crew_name: "John Martinez",
          role: "Captain"
        }
      ],
      can_substitute: true
    },
    {
      booking_id: "12345671",
      booking_uuid: "ghi-789",
      experience_name: "Shell Island Snorkel Tour",
      client_name: "Noah Miller",
      booked_date: "09/22/2025",
      start_time: "04:00 PM",
      end_time: "05:00 PM",
      status: "booked",
      guest_count: 5,
      available_substitutes: [
        {
          crew_id: 42,
          crew_name: "Boston Whaler 405 Con...",
          role: "Captain"
        },
        {
          crew_id: 43,
          crew_name: "John Martinez",
          role: "Captain"
        }
      ],
      can_substitute: true
    }
  ],
  requires_substitution: true,
  exp_message: "By deactivating Alex White he will be removed from all assigned experiences availability. In addition, Alex will lose access to the system until reactivated."
}

// Scenario 4: Partially substitutable - Some bookings need manual replacement or cannot be substituted
export const MOCK_PARTIALLY_SUBSTITUTABLE = {
  success: true,
  can_deactivate: false,
  status: "partially_substitutable",
  crew_id: 126,
  crew_name: "Alex White",
  role: "Captain",
  assigned_trips: [
    {
      trip_id: 45,
      trip_name: "Meet Sunset at Panama City",
      trip_category: "Sightseeing",
      guide_name: "John Doe",
      min_guests: 1,
      max_guests: 6
    },
    {
      trip_id: 46,
      trip_name: "Shell Island Snorkel Tour",
      trip_category: "Snorkeling",
      guide_name: "John Doe",
      min_guests: 2,
      max_guests: 8
    },
    {
      trip_id: 47,
      trip_name: "Dolphin Watch – Gulf Shores",
      trip_category: "Wildlife",
      guide_name: "John Doe",
      min_guests: 1,
      max_guests: 6
    }
  ],
  active_bookings: [
    {
      booking_id: "12345678",
      booking_uuid: "abc-123",
      experience_name: "Meet Sunset at Panama City",
      client_name: "Tom Smith",
      booked_date: "09/20/2025",
      start_time: "04:00 PM",
      end_time: "08:00 PM",
      status: "booked",
      guest_count: 4,
      available_substitutes: [
        {
          crew_id: 42,
          crew_name: "Boston Whaler 405 Con...",
          role: "Captain"
        }
      ],
      can_substitute: true
    },
    {
      booking_id: "12345671",
      booking_uuid: "def-456",
      experience_name: "Dolphin Watch – Gulf Shores",
      client_name: "Ava Johnson",
      booked_date: "09/21/2025",
      start_time: "05:00 PM",
      end_time: "06:00 PM",
      status: "booked",
      guest_count: 6,
      available_substitutes: [
        {
          crew_id: 42,
          crew_name: "Boston Whaler 405 Con...",
          role: "Captain"
        }
      ],
      can_substitute: true
    },
    {
      booking_id: "12345671",
      booking_uuid: "ghi-789",
      experience_name: "Shell Island Snorkel Tour",
      client_name: "Noah Miller",
      booked_date: "09/22/2025",
      start_time: "04:00 PM",
      end_time: "05:00 PM",
      status: "booked",
      guest_count: 8,
      available_substitutes: [],
      can_substitute: false
    }
  ],
  requires_substitution: true,
  booking_message: "There are no crew available for this booking. Alex can not be deactivated until this booking is reassigned/cancelled/ rescheduled.",
  exp_message: "By deactivating Alex White he will be removed from all assigned experiences availability. In addition, Alex will lose access to the system until reactivated.",
  blocking_bookings: [
    {
      booking_id: "12345671",
      booking_uuid: "ghi-789",
      experience_name: "Shell Island Snorkel Tour",
      client_name: "Noah Miller",
      booked_date: "09/22/2025",
      start_time: "04:00 PM",
      end_time: "05:00 PM"
    }
  ]
}

/**
 * Get mock data based on crew member ID for testing
 * @param {number} crewId
 * @returns {Object} Mock deactivation details
 */
export const getMockDeactivationDetails = (crewId) => {
  // Different IDs return different scenarios for testing
  const scenarios = {
    1: MOCK_NO_EXPERIENCES,
    2: MOCK_ASSIGNED_EXPERIENCES_NO_BOOKINGS,
    3: MOCK_FULLY_SUBSTITUTABLE,
    4: MOCK_PARTIALLY_SUBSTITUTABLE
  }

  // Default to partially substitutable for testing all features
  return scenarios[crewId] || MOCK_PARTIALLY_SUBSTITUTABLE
}
